/**
 * Legacy affiliate-link `a_aid` → numeric AFID resolution (SFCC-11532).
 *
 * Some TPA affiliate links carry `a_aid=<PAP hash>` instead of the usual
 * numeric `AFID`. SFRA resolves the hash to a LinkTrust AFID via a static
 * mapping (`int_linktrust_sfra/.../linkTrustHelper.js` + `linktrustPAP.json`)
 * and sets the attribution cookie. Composable never resolved `a_aid`, so after
 * 100% composable turn-on these links dropped the TPA cookie on the homepage.
 *
 * This is the faithful client-side port of SFRA's `a_aid` branch: look up the
 * hash, pick the env-specific LinkTrust ID, and return revenue-cookie params
 * (affiliate → tpa slot, broker → broker slot) with SFRA's hardcoded campaign
 * IDs. Branding is deliberately NOT produced here — SFRA sets only the revenue
 * cookie for `a_aid` affiliates (partner branding is driven by `GroupName`).
 *
 * `linktrustPAP.json` is vendored from SFRA
 * (`int_linktrust_sfra/cartridge/config/linktrustPAP.json`) — SFRA is the source
 * of truth; re-sync this copy when affiliates are added/changed.
 */
import linktrustPapRaw from './linktrustPAP.json'
import {TpaGroupNames} from './types'

interface LinktrustEntry {
    papID: string
    aid: string
    stgLinktrustID: string
    prodLinktrustID: string
    isBroker: string
}

// Campaign IDs hardcoded in SFRA `linkTrustHelper.SETTINGS` (used as cookie CID).
const SETTINGS = {
    STG_AFFILIATE: '437555',
    STG_BROKER: '437422',
    PROD_AFFILIATE: '437559',
    PROD_BROKER: '437560'
} as const

// First-wins on duplicate `aid`, matching SFRA's `.filter(...)[0]` and the
// Workers port (linktrust.server.ts). A plain `new Map(rows.map(...))` would be
// last-wins; harmless today (the only dup, BD2392, is byte-identical) but a
// future re-sync could land a non-identical dup and silently diverge from SFRA.
const PAP_BY_AID = new Map<string, LinktrustEntry>()
for (const entry of linktrustPapRaw as LinktrustEntry[]) {
    if (!PAP_BY_AID.has(entry.aid)) PAP_BY_AID.set(entry.aid, entry)
}

export interface ResolvedAffiliate {
    afid: string
    groupName: string
    cid: string
}

/**
 * Resolve an `a_aid` PAP hash to revenue-cookie params, or null if unknown.
 * @param aid    the `a_aid` URL value (PAP hash)
 * @param isProd true on the production storefront (selects prod vs stg IDs)
 */
export const resolveAaidAffiliate = (aid: string, isProd: boolean): ResolvedAffiliate | null => {
    const entry = PAP_BY_AID.get(aid)
    if (!entry) return null

    const afid = isProd ? entry.prodLinktrustID : entry.stgLinktrustID
    if (!afid) return null

    if (entry.isBroker === 'Yes') {
        return {
            afid,
            groupName: TpaGroupNames.BROKER,
            cid: isProd ? SETTINGS.PROD_BROKER : SETTINGS.STG_BROKER
        }
    }
    return {
        afid,
        groupName: TpaGroupNames.TPA,
        cid: isProd ? SETTINGS.PROD_AFFILIATE : SETTINGS.STG_AFFILIATE
    }
}
